import { useEffect, useState } from "react";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatDate, formatTime } from "../../lib/time";
import { callerIdDevice, type CallerIdStatus } from "./caller-id";
import { useCallerIdLog, useCallerIdStatus } from "./useCallerIdDevice";

interface Props {
  /** The owner's Beheer code: the network address is the owner's. */
  staffToken: string;
}

const PILL: Partial<Record<CallerIdStatus, string>> = {
  on: "ok",
  connecting: "warn",
  silent: "warn",
  absent: "warn",
  busy: "warn",
  unsupported: "bad",
};

/**
 * Beheer › Printers & toestellen › Nummerherkenning (besluiten: the USB device on the telephone line, as
 * now, or over the network with a VoIP exchange): the device of this till pc, and the shop's network
 * address for an exchange or IP phone.
 */
export function CallerIdSection({ staffToken }: Props) {
  const { api, toast, report } = useTill();
  const text = t.calls.setup;
  const status = useCallerIdStatus();
  const lines = useCallerIdLog();
  const [hook, setHook] = useState<{ active: boolean; createdAt: string | null } | null>(null);
  /** Shown once, right after it was made. */
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    api
      .request<{ active: boolean; createdAt: string | null }>("GET", "/device/calls/hook", { staffToken })
      .then((answer) => {
        if (current) setHook(answer);
      })
      .catch(report);
    return () => {
      current = false;
    };
  }, [api, report, staffToken]);

  const makeAddress = async () => {
    try {
      const answer = await api.request<{ url: string }>("POST", "/device/calls/hook", { staffToken });
      setUrl(answer.url);
      setHook({ active: true, createdAt: new Date().toISOString() });
    } catch (error) {
      report(error);
    }
  };

  const switchOff = async () => {
    try {
      await api.request("DELETE", "/device/calls/hook", { staffToken });
      setUrl(null);
      setHook({ active: false, createdAt: null });
      toast(text.switchedOff);
    } catch (error) {
      report(error);
    }
  };

  const pill = PILL[status];
  return (
    <>
      <div className="zone">{t.printing.zones.callerId}</div>
      <p className="note">{text.note}</p>
      <div className="tablewrap">
        <table className="ptbl callerid">
          <tbody>
            <tr>
              <td>
                <b>{text.usb}</b>
                <br />
                <span className="note">{text.usbWhere}</span>
              </td>
              <td>{pill ? <span className={`pill ${pill}`}>{text.status[status]}</span> : text.status[status]}</td>
              <td className="r">
                {status !== "unsupported" && (
                  <>
                    <button type="button" className="btn small" onClick={() => void callerIdDevice.choose()}>
                      {text.choose}
                    </button>{" "}
                    {status !== "off" && (
                      <button
                        type="button"
                        className="btn small"
                        onClick={() => {
                          void callerIdDevice.forget().then(() => {
                            toast(text.unlinked);
                          });
                        }}
                      >
                        {text.unlink}
                      </button>
                    )}
                  </>
                )}
              </td>
            </tr>
            <tr>
              <td>
                <b>{text.network}</b>
                <br />
                <span className="note">{text.network2}</span>
              </td>
              <td>
                {hook &&
                  (hook.active && hook.createdAt !== null ? (
                    <span className="pill ok">{text.networkOn(formatDate(hook.createdAt))}</span>
                  ) : (
                    text.networkOff
                  ))}
              </td>
              <td className="r">
                {hook && (
                  <>
                    <button type="button" className="btn small" onClick={() => void makeAddress()}>
                      {hook.active ? text.newAddress : text.makeAddress}
                    </button>{" "}
                    {hook.active && (
                      <button type="button" className="btn small" onClick={() => void switchOff()}>
                        {text.switchOff}
                      </button>
                    )}
                  </>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {lines.length > 0 && (
        <details className="devicelog">
          <summary>{text.log}</summary>
          <p className="note">{text.logNote}</p>
          <pre>
            {lines
              .map(
                (line) =>
                  `${formatTime(new Date(line.at).toISOString())}  ${line.direction === "out" ? "→" : "←"}  ${line.text}`,
              )
              .join("\n")}
          </pre>
        </details>
      )}
      {url !== null && (
        <div className="hookurl">
          <p className="note">{text.addressNote}</p>
          <div className="inl">
            <input readOnly value={url} aria-label={text.network} onFocus={(event) => event.target.select()} />
            <button
              type="button"
              className="btn"
              onClick={() => {
                void navigator.clipboard
                  .writeText(url)
                  .then(() => {
                    toast(text.copied);
                  })
                  .catch(() => undefined);
              }}
            >
              {text.copy}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
